import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement
} from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import ResponsiveButton from "../ResponsiveButton/ResponsiveButton";

// ✅ Stripe-objekti ladattuna julkisella avaimella
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// ✅ Lomake, jossa vahvistetaan maksu
function CheckoutInner({ onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(""); // Kortinhaltijan nimi

  // ✅ Maksun lähetys Stripeen
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      toast.error("Maksutoiminto ei ole valmis.");
      return;
    }

    if (!name.trim()) {
      toast.error("Syötä kortinhaltijan nimi.");
      return;
    }

    setLoading(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href, // fallback jos redirect käytössä
        payment_method_data: {
          billing_details: {
            name: name.trim(), // ✅ Kortinhaltijan nimi mukaan maksutietoihin
          },
        },
      },
      redirect: "if_required"
    });

    if (error) {
      toast.error(`Maksu epäonnistui: ${error.message}`);
    } else if (paymentIntent?.status === "succeeded") {
      toast.success("Maksu onnistui!");
      onSuccess();
    } else {
      toast.error("Maksua ei vahvistettu.");
    }

    setLoading(false);
  };

  // Ladataan Stripe-kirjasto
  if (!stripe || !elements) {
    return <p>Ladataan maksutoiminto…</p>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 w-full  text-darkest font-customFont1 bg-white p-4 rounded-3xl">
      {/* ✅ Kortinhaltijan nimi -kenttä */}
      <div>
        <label htmlFor="cardholder-name" className="block mb-1">
          Kortinhaltijan nimi
        </label>
        <input
          type="text"
          id="cardholder-name"
          placeholder="Etunimi Sukunimi"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="w-full p-3 rounded-full bg-pr bg-darkest border-none focus:outline-none text-lg pl-5 text-white"
        />
      </div>

      {/* ✅ Stripein maksuelementti – vain korttimaksu näkyvissä */}
      <PaymentElement
        options={{
          layout: "tabs",
          paymentMethodOrder: ['card'], // piilottaa muut kuin kortin
          business: { name: "Spiderlegs Productions" },
        }}
      />

      {/* ✅ Maksupainike */}
      <ResponsiveButton type="submit" disabled={loading} className="w-full text-darkest font-customFont1">
        {loading ? "Käsittelen…" : "Maksa nyt"}
      </ResponsiveButton>
    </form>
  );
}

CheckoutInner.propTypes = {
  onSuccess: PropTypes.func.isRequired,
};

// ✅ Ulkoinen komponentti joka hakee maksun clientSecretin
export default function SubscriptionCheckoutForm({ plan, amount, onSuccess }) {
  const [clientSecret, setClientSecret] = useState(null);

  useEffect(() => {
    if (!plan || !amount || clientSecret) return;

    // ✅ Haetaan maksuintention clientSecret PHP-backendiltä
    (async () => {
      try {
        const response = await fetch("https://spiderlegsproductions.com/api/create_subscription_intent.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ plan, amount })
        });

        const data = await response.json();

        if (data.clientSecret) {
          setClientSecret(data.clientSecret);
        } else {
          throw new Error(data.message || "Maksuvalmistelu epäonnistui");
        }
      } catch (error) {
        console.error("Maksuintentio epäonnistui:", error);
        toast.error("Maksuvalmistelu epäonnistui.");
      }
    })();
  }, [plan, amount, clientSecret]);

  // Näytetään latausteksti jos maksua ei voi vielä näyttää
  if (!clientSecret) {
    return <p className='text-darkest dark:text-primary dark:text-shadow-lg font-customFont1'>Valmistelen maksua…</p>;
  }

  // ✅ Kun maksuintentio valmis, näytetään lomake
  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <CheckoutInner onSuccess={onSuccess} />
    </Elements>
  );
}

SubscriptionCheckoutForm.propTypes = {
  plan: PropTypes.oneOf(["basic", "premium"]).isRequired,
  amount: PropTypes.number.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
